/**
 * The appointment status EVERY gate on the encounter shell reads — Start, the vitals editor, the
 * chief-complaint lock, and the status pill itself.
 *
 * THE FETCHED APPOINTMENT WINS; nav is only the first-paint fallback (PROD-2030).
 *
 * This order was the other way round, and it made the status dropdown look broken: change Booked →
 * Arrived on the pre-encounter and nothing re-gated — no vitals `+`, chief complaint still locked,
 * Start still disabled (Andrew, dev, 2026-08-25). The write and the refetch were both working. The
 * READ defeated them: `nav.appointmentStatus` is a TAP-TIME SNAPSHOT taken when the row was clicked
 * in Today/Calendar, and it can only ever go stale. Preferring it meant the freshly refetched
 * "Arrived" was never consulted. On a deep-link there is no nav, which is why the bug only showed
 * when arriving by tap.
 *
 * Flutter has no such snapshot to prefer: there is ONE `state.appointment`, always the fetched one,
 * and every gate derives from `appointment.status.code`. Its dropdown handler PUTs and then
 * immediately re-fetches — `add(FetchAppointmentDetailEvent())`,
 * patient_encounter_bloc.dart:1749-1765 — and its SignalR listener replaces the same object from a
 * pushed payload (`UpdateAppointments` → `UpdateAppointment(appt)`, encounter_screen.dart:260-270).
 * Both paths converge on one value. This makes ours converge too: the query is invalidated by the
 * status mutation, so the refetch now actually reaches the gates.
 *
 * This lives here rather than inline in `encounter-shell-page.tsx` so the rule is REACHABLE BY TEST.
 * As an inline expression it was untestable in practice: every page test mocks `useAppointmentStatus`
 * to `{ data: null }`, under which nav wins on EITHER ordering, so the two branches are
 * indistinguishable and the bug could be reintroduced with the whole suite green. The unit test
 * beside this file now exercises this exact function — it cannot drift from the page.
 *
 * @param fetched     `appointmentStatusQuery.data?.appointmentStatus` — server truth, re-fetched on
 *                    every status mutation.
 * @param navSnapshot `nav?.appointmentStatus` — the tap-time router-state snapshot; absent on a
 *                    deep-link.
 * @returns the status every gate should read, or `undefined` when neither source has one.
 */
export function resolveAppointmentStatus(
  fetched: string | null | undefined,
  navSnapshot: string | null | undefined,
): string | undefined {
  return fetched ?? navSnapshot ?? undefined;
}
